import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Spinner, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type MyRecord, type RecordMark } from '../lib/runnerApiClient';

/**
 * Your record (§6c) — the portal twin of the Runner app's Record screen.
 *
 * 🔒 Nothing on this page is automatic, and it says so. Three marks in the
 * window gets an account read by a person; five pauses it while we talk. The
 * page shows the facts and never computes a score.
 *
 * 🛑 "What is not a mark" is as prominent as the marks. A Runner unsure whether
 * passing on an open run counts against them stops taking cover offers.
 *
 * Mark labels and the not-a-mark list come from runner-api as written.
 */

const NAVY = '#343F5B';
const GREEN = '#2E9E3A';
const RED = '#b91c1c';
const AMBER = '#F67A1A';
const GREY = '#cbd5e1';

function dayLabel(ymd: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${ymd}T00:00:00.000Z`));
}

export default function Record() {
  const { t } = useTranslation();
  const [record, setRecord] = useState<MyRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    runnerApi
      .myRecord()
      .then((d) => !cancelled && setRecord(d))
      .catch((e) => !cancelled && setError(e instanceof RunnerApiError ? e.message : t('record.loadFailed')));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const inWindow = (record?.marks ?? []).filter((m) => m.inWindow);
  const aged = (record?.marks ?? []).filter((m) => !m.inWindow);

  return (
    <Box data-testid="page-record">
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' } }}>
        {t('record.title')}
      </Typography>
      <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5, mb: 2.5, maxWidth: 720 }}>{t('record.subtitle')}</Typography>

      {error ? (
        <Alert severity="error" data-testid="record-error">{error}</Alert>
      ) : record === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }} aria-label={t('record.loading')}>
          <Spinner />
        </Box>
      ) : (
        <Box data-testid="record-loaded" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.3fr' }, gap: 2.5, alignItems: 'start' }}>
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Panel
              accent={NAVY}
              title={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  {t('record.lastDays', { days: record.windowDays })}
                  {record.stage ? (
                    <Chip label={t(`record.tier.${record.stage}`, { defaultValue: record.stage })} size="small" backgroundColor="#dcfce7" textColor="#15803d" fontWeight={700} />
                  ) : null}
                </Box>
              }
            >
              <Stat testId="record-deliveries" label={t('record.deliveries')} sub={t('record.deliveriesSub')} value={String(record.deliveries30)} />
              <Stat
                testId="record-satisfaction"
                label={t('record.satisfaction')}
                sub={record.ratingCount > 0 ? t('record.satisfactionSub') : t('record.noRatings')}
                value={record.satisfaction === null ? '—' : `${record.satisfaction}%`}
              />
              <Stat
                testId="record-marks"
                label={t('record.marks')}
                sub={inWindow.length > 0 ? (inWindow[0]?.label ?? '') : t('record.noMarks')}
                value={String(record.marks30)}
                danger={record.marks30 > 0}
                last
              />
            </Panel>

            <Panel accent={AMBER}>
              <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{t('record.thresholds', { days: record.windowDays })}</Typography>
            </Panel>

            <Panel accent={GREEN} title={t('record.notMarkTitle')} dataTestId="record-not-marks">
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1 }}>
                {record.notMarks.map((line) => (
                  <Box component="li" key={line} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Typography component="span" aria-hidden sx={{ color: GREEN, fontWeight: 800 }}>
                      ✓
                    </Typography>
                    <Typography component="span" sx={{ color: '#4b5563', fontSize: '14px' }}>
                      {line}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Panel>
          </Box>

          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Panel accent={inWindow.length > 0 ? RED : NAVY} title={t('record.onRecordTitle')} dataTestId="record-on-record">
              {inWindow.length === 0 ? (
                <Typography sx={{ color: '#5f6b7a', fontSize: '14px' }}>{t('record.noMarks')}</Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {inWindow.map((m) => (
                    <MarkRow key={m.id} mark={m} />
                  ))}
                </Box>
              )}
            </Panel>

            {aged.length > 0 ? (
              <Panel accent={GREY} title={t('record.olderTitle')} dataTestId="record-older">
                <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('record.olderNote', { days: record.windowDays })}</Typography>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {aged.map((m) => (
                    <MarkRow key={m.id} mark={m} aged />
                  ))}
                </Box>
              </Panel>
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Stat({ label, sub, value, danger, last, testId }: { label: string; sub: string; value: string; danger?: boolean; last?: boolean; testId?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, borderBottom: last ? 'none' : '1px solid #e5e7eb' }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ color: '#1f2937', fontSize: '15px', fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{sub}</Typography>
      </Box>
      <Typography data-testid={testId ? `${testId}-value` : undefined} sx={{ color: danger ? RED : '#1f2937', fontSize: '24px', fontWeight: 800 }}>{value}</Typography>
    </Box>
  );
}

function MarkRow({ mark, aged }: { mark: RecordMark; aged?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  return (
    <Box data-testid={`record-mark-${mark.id}`} sx={{ borderLeft: `3px solid ${aged ? GREY : RED}`, pl: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography component="h3" sx={{ m: 0, color: '#1f2937', fontSize: '15px', fontWeight: 700 }}>
          {mark.label}
        </Typography>
        <Chip
          label={aged ? t('record.agedOut') : t('record.onYourRecord')}
          size="small"
          backgroundColor={aged ? '#eef2f6' : '#fee2e2'}
          textColor={aged ? '#4b5563' : RED}
          fontWeight={700}
        />
      </Box>
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{dayLabel(mark.occurredOn, locale)}</Typography>
      {mark.noticeHours !== null ? (
        <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>
          {t(mark.noticeHours === 1 ? 'record.noticeHour' : 'record.noticeHours', { count: mark.noticeHours })}
          {' · '}
          {t(mark.orderCount === 1 ? 'record.oneDelivery' : 'record.deliveriesCount', { count: mark.orderCount })}
        </Typography>
      ) : null}
    </Box>
  );
}
